<script lang="ts">
import { getContext } from 'svelte';

import { Button, Icon } from '@viamrobotics/prime-core';
import { clickOutside } from '@viamrobotics/prime-core';

import type { AuthState } from '$lib/auth';
import { popupEnter } from '$lib/components/floating/popup-enter';
import { createInkeepWidget, type InkeepWidget } from '$lib/inkeep';

interface Props {
  authState: AuthState;
}

const { authState }: Props = $props();

let isOpen = $state(false);

const closeMenu = () => {
  isOpen = false;
};

let inkeep: InkeepWidget | undefined = $state(undefined);

const initialize = async () => {
  if (authState) {
    inkeep = await createInkeepWidget(authState);
    inkeep?.initialize();
  }
};

$effect.pre(() => {
  void initialize();
});

const openSidebar =
  getContext<(inkeep: InkeepWidget | undefined) => void>('openSidebar');

const handleAskAI = () => {
  openSidebar(inkeep);
  closeMenu();
};
</script>

<div
  class="relative z-max flex self-stretch font-public-sans"
  use:clickOutside={closeMenu}
>
  <button
    aria-label="Support"
    onclick={() => {
      isOpen = !isOpen;
    }}
    class="flex items-center justify-center h-8 w-8 bg-light hover:bg-medium {isOpen
      ? 'bg-medium'
      : ''}"
  >
    <Icon
      name="help-circle-outline"
      class="text-gray-7 w-4 h-4"
    />
  </button>

  {#if isOpen}
    <article
      use:popupEnter
      class="absolute right-0 top-full -mr-1 mt-4 flex max-h-[80vh] w-80 origin-top-right flex-col items-stretch overflow-y-auto border border-medium bg-white text-default shadow-sm"
    >
      <div class="border-b border-b-light p-4 text-left">
        <h2 class="text-base font-semibold">Stuck on something?</h2>
        <p class="text-sm text-subtle-1">
          Try our AI assistant to get immediate help.
        </p>
        <Button
          icon="ask-ai"
          class="w-full mt-4 h-9"
          onclick={handleAskAI}>Ask AI</Button
        >
      </div>

      <nav class="flex flex-col py-2 text-sm text-subtle-1">
        <a
          href="https://status.viam.com"
          target="_blank"
          rel="external"
          class="flex items-center gap-3 px-4 py-1.5 hover:bg-medium"
          onclick={closeMenu}
        >
          <div class="text-gray-6">
            <Icon name="pulse" />
          </div>
          Platform status
        </a>

        <a
          href="https://docs.viam.com/dev/reference/changelog/"
          target="_blank"
          rel="external"
          class="flex items-center gap-3 px-4 py-1.5 hover:bg-medium"
          onclick={closeMenu}
        >
          <div class="text-gray-6">
            <Icon name="megaphone" />
          </div>
          Changelog
        </a>

        <a
          href="https://viam.atlassian.net/servicedesk/customer/portal/7/group/17/create/65"
          target="_blank"
          rel="external"
          class="flex items-center gap-3 px-4 py-1.5 hover:bg-medium"
          onclick={closeMenu}
        >
          <div class="text-gray-6">
            <Icon name="inbox" />
          </div>
          Contact Support
        </a>
      </nav>
    </article>
  {/if}
</div>
